/**
 * Outpost System Configuration
 *
 * Outposts are player-built settlements on world map tiles featuring:
 * - Custom naming
 * - 4 building slots (vs 1 on regular tiles)
 * - Trade route connection to capital
 * - Mercenary hiring and management
 */

// Re-export mercenary and mission configs
export * from './mercenaries';
export * from './missions';

/**
 * Core outpost configuration
 */
export const OUTPOST_CONFIG = {
  // Construction
  constructionTime: 30000,      // 30 seconds for testing (was 5 minutes)
  maxBuildingSlots: 4,          // Can build 4 buildings per outpost
  maxOutpostsTotal: 3,          // Maximum outposts player can have (expandable via techs later)

  // Mercenary capacity
  maxMercenariesPerOutpost: 6,  // Maximum mercs stationed at one outpost
  maxSquadSize: 4,              // Maximum mercs per squad
};

/**
 * Construction costs per biome type
 * Player must pay these resources to start outpost construction
 */
export const OUTPOST_COSTS = {
  // Tier 1 biomes (cheap)
  forest: { wood: 200, stone: 100, gold: 50 },
  plains: { wood: 150, food: 150, gold: 50 },

  // Tier 2 biomes (moderate)
  mountains: { wood: 200, stone: 300, gold: 100 },
  coastal: { wood: 300, gold: 150 },
  hills: { wood: 200, stone: 200, gold: 75 },

  // Tier 3 biomes (expensive)
  ruins: { stone: 400, knowledge: 150, gold: 200 },
  volcanic: { stone: 500, iron: 200, coal: 150, gold: 200 },
  swamp: { wood: 400, food: 200, gold: 150 },

  // Tier 4 biomes (very expensive)
  mysticGrove: { wood: 600, magic: 150, gold: 300 },
  desert: { stone: 500, gold: 400 },
  tundra: { wood: 500, food: 300, gold: 250 },

  // Tier 5 biomes (extremely expensive)
  voidRift: { stone: 800, magic: 300, energy: 100, gold: 500 },
  crystalCavern: { stone: 700, gems: 50, gold: 400 },

  // Default fallback for any biome not listed
  default: { wood: 300, stone: 200, gold: 150 },
};

/**
 * Get construction cost for a specific biome
 * @param {string} biomeId - The biome type
 * @returns {Object} Resource costs
 */
export function getOutpostCost(biomeId) {
  return OUTPOST_COSTS[biomeId] || OUTPOST_COSTS.default;
}

/**
 * Building slots configuration
 * Each slot can hold one building from the biome's associated buildings
 */
export const BUILDING_SLOT_CONFIG = {
  // Slot bonuses when all slots are filled
  fullOutpostBonus: {
    productionMultiplier: 1.25,   // +25% production from all buildings
    gatherMultiplier: 1.10,       // +10% gather yield
  },

  // Per-slot production multiplier
  slotProductionMultiplier: 1.0,  // Each building produces at 100% rate
};

/**
 * Outpost passive bonuses (applied while outpost exists)
 */
export const OUTPOST_BONUSES = {
  // Trade route bonus when connected
  tradeRouteConnected: {
    resourceTransferRate: 1.0,    // Full resource transfer to kingdom
    goldBonus: 0.10,              // +10% gold from all sources
  },

  // Vision bonus (reveals more tiles around outpost)
  visionRange: 2,                 // Reveals tiles within 2 hexes
};

/**
 * Check if a tile is valid for outpost construction
 * @param {Object} tileData - The tile data from discoveredNodes
 * @param {Object} worldMap - Current world map state
 * @returns {{ valid: boolean, reason?: string }}
 */
export function canBuildOutpost(tileData, worldMap) {
  if (!tileData) {
    return { valid: false, reason: 'Invalid tile' };
  }

  if (tileData.isHub) {
    return { valid: false, reason: 'Cannot build outpost on Kingdom' };
  }

  if (tileData.isDungeonBiome) {
    return { valid: false, reason: 'Cannot build outpost on dungeon tiles' };
  }

  if (tileData.isEncounterBiome) {
    return { valid: false, reason: 'Cannot build outpost on encounter tiles' };
  }

  if (tileData.isKingdomBiome) {
    return { valid: false, reason: 'Cannot build outpost near other kingdoms' };
  }

  if (tileData.isTowerBiome) {
    return { valid: false, reason: 'Cannot build outpost on tower tiles' };
  }

  // Check if this is a wonder site tile
  if (tileData.biome === 'wonderSite' || tileData.wonderSiteId) {
    return { valid: false, reason: 'Cannot build outpost on Wonder Sites' };
  }

  // Check if outpost already exists at this tile
  const nodeId = tileData.id;
  if (worldMap.outposts && worldMap.outposts[nodeId]) {
    return { valid: false, reason: 'Outpost already exists here' };
  }

  // Check total outpost count
  const currentOutpostCount = Object.keys(worldMap.outposts || {}).length;
  if (currentOutpostCount >= OUTPOST_CONFIG.maxOutpostsTotal) {
    return { valid: false, reason: `Maximum ${OUTPOST_CONFIG.maxOutpostsTotal} outposts reached` };
  }

  return { valid: true };
}

/**
 * Format construction time for display
 * @param {number} ms - Time in milliseconds
 * @returns {string} Formatted time string
 */
export function formatConstructionTime(ms) {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${seconds}s`;
}
